import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  School as SchoolIcon,
  Payments as PaymentsIcon,
  AttachMoney as AttachMoneyIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { esES } from '@mui/x-data-grid/locales';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { teacherService } from '../../services/api';
import { exportToExcel } from '../../utils/excelExport';

interface Teacher {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  hourly_rate: number;
  status: 'active' | 'inactive';
  specialization: string | null;
  total_paid?: number;
  total_hours?: number;
  last_payment?: string;
}

const TeacherList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });
  
  // Filtros
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    specialization: ''
  });
  
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkRateDialogOpen, setBulkRateDialogOpen] = useState(false);
  const [bulkRateData, setBulkRateData] = useState({
    hourly_rate: 0,
    teacher_ids: [] as number[]
  });

  // Estadísticas
  const [stats, setStats] = useState({
    totalActive: 0,
    totalInactive: 0,
    averageRate: 0,
    totalPaidThisMonth: 0
  });

  useEffect(() => {
    loadTeachers();
  }, [paginationModel, filters]);

  const loadTeachers = async () => {
    try {
      setLoading(true);
      
      const params: any = {
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
        ...filters
      };
      
      // Limpiar filtros vacíos
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await teacherService.getAll(params);
      
      setTeachers(response.data.data);
      setTotal(response.data.total);
      
      // Calcular estadísticas
      const active = response.data.data.filter((t: Teacher) => t.status === 'active');
      const inactive = response.data.data.filter((t: Teacher) => t.status === 'inactive');
      const avgRate = active.reduce((sum: number, t: Teacher) => sum + t.hourly_rate, 0) / (active.length || 1);
      
      setStats({
        totalActive: active.length,
        totalInactive: inactive.length,
        averageRate: avgRate,
        totalPaidThisMonth: response.data.data.reduce((sum: number, t: Teacher) => sum + (t.total_paid || 0), 0)
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar profesores');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTeacher) return;
    
    try {
      await teacherService.delete(selectedTeacher.id);
      loadTeachers();
      setDeleteDialogOpen(false);
      setSelectedTeacher(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar profesor');
    }
  };

  const handleToggleStatus = async (teacher: Teacher) => {
    try {
      if (teacher.status === 'active') {
        await teacherService.deactivate(teacher.id);
      } else {
        await teacherService.activate(teacher.id);
      }
      loadTeachers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cambiar estado');
    }
  };

  const handleBulkRateUpdate = async () => {
    try {
      await teacherService.bulkUpdateRates(bulkRateData);
      setBulkRateDialogOpen(false);
      loadTeachers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al actualizar tarifas');
    }
  };

  const handleExport = () => {
    const data = teachers.map(teacher => ({
      'ID': teacher.id,
      'Nombre': `${teacher.first_name} ${teacher.last_name}`,
      'Email': teacher.email,
      'Teléfono': teacher.phone || '',
      'Tarifa por Hora': teacher.hourly_rate,
      'Especialización': teacher.specialization || '',
      'Estado': teacher.status === 'active' ? 'Activo' : 'Inactivo',
      'Total Pagado': teacher.total_paid || 0,
      'Total Horas': teacher.total_hours || 0
    }));
    
    exportToExcel(data, 'profesores');
  };

  const getStatusChip = (status: string) => {
    return status === 'active' ? (
      <Chip
        icon={<CheckCircleIcon />}
        label="Activo"
        color="success"
        size="small"
      />
    ) : (
      <Chip
        icon={<WarningIcon />}
        label="Inactivo"
        color="default"
        size="small"
      />
    );
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'name',
      headerName: 'Nombre',
      width: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'secondary.main' }}>
            <PersonIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {params.row.first_name} {params.row.last_name}
            </Typography>
            {params.row.specialization && (
              <Typography variant="caption" color="text.secondary">
                {params.row.specialization}
              </Typography>
            )}
          </Box>
        </Box>
      )
    },
    {
      field: 'contact',
      headerName: 'Contacto',
      width: 200,
      renderCell: (params) => (
        <Box>
          <Box display="flex" alignItems="center">
            <EmailIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="body2">{params.row.email}</Typography>
          </Box>
          {params.row.phone && (
            <Box display="flex" alignItems="center" sx={{ mt: 0.5 }}>
              <PhoneIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
              <Typography variant="body2">{params.row.phone}</Typography>
            </Box>
          )}
        </Box>
      )
    },
    {
      field: 'hourly_rate',
      headerName: 'Tarifa/Hora',
      width: 120,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium" color="primary">
          ₽{params.value.toLocaleString()}
        </Typography>
      )
    },
    {
      field: 'total_hours',
      headerName: 'Horas',
      width: 80,
      align: 'center',
      renderCell: (params) => (
        <Chip
          label={params.value || 0}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'total_paid',
      headerName: 'Total Pagado',
      width: 120,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium" color="success.main">
          ₽{(params.value || 0).toLocaleString()}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 100,
      renderCell: (params) => getStatusChip(params.value)
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver detalles">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/teachers/${params.row.id}`);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/teachers/${params.row.id}/edit`);
              }}
              disabled={!['admin', 'manager'].includes(user?.role || '')}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.status === 'active' ? 'Desactivar' : 'Activar'}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleToggleStatus(params.row);
              }}
              color={params.row.status === 'active' ? 'warning' : 'success'}
              disabled={!['admin', 'manager'].includes(user?.role || '')}
            >
              {params.row.status === 'active' ? <WarningIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedTeacher(params.row);
                setDeleteDialogOpen(true);
              }}
              disabled={!['admin', 'manager'].includes(user?.role || '')}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Profesores
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ mr: 1 }}
          >
            Exportar
          </Button>
          <Button
            variant="outlined"
            startIcon={<AttachMoneyIcon />}
            onClick={() => setBulkRateDialogOpen(true)}
            sx={{ mr: 1 }}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Actualizar Tarifas
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/teachers/new')}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Nuevo Profesor
          </Button>
        </Box>
      </Box>

      {/* Estadísticas */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 3, sm: 6 }} >
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Profesores Activos
                  </Typography>
                  <Typography variant="h4">
                    {stats.totalActive}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.light', width: 48, height: 48 }}>
                  <PersonIcon />
                </Avatar>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(stats.totalActive / (stats.totalActive + stats.totalInactive)) * 100} 
                sx={{ mt: 2, height: 6, borderRadius: 3 }}
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, md: 3, sm: 6 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Tarifa Promedio
                  </Typography>
                  <Typography variant="h4" color="primary">
                    ₽{stats.averageRate.toFixed(0)}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.light', width: 48, height: 48 }}>
                  <AttachMoneyIcon />
                </Avatar>
              </Box>
              <Typography variant="caption" color="text.secondary">
                por hora
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, md: 3, sm: 6 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Pagado este Mes
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    ₽{stats.totalPaidThisMonth.toLocaleString()}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.light', width: 48, height: 48 }}>
                  <PaymentsIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, md: 3, sm: 6 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Horas
                  </Typography>
                  <Typography variant="h4">
                    {teachers.reduce((sum, t) => sum + (t.total_hours || 0), 0)}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.light', width: 48, height: 48 }}>
                  <TrendingUpIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              placeholder="Buscar profesor..."
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={filters.status}
                label="Estado"
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="active">Activos</MenuItem>
                <MenuItem value="inactive">Inactivos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              placeholder="Especialización"
              value={filters.specialization}
              onChange={(e) => setFilters({...filters, specialization: e.target.value})}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<FilterListIcon />}
                onClick={() => loadTeachers()}
                fullWidth
              >
                Filtrar
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setFilters({ search: '', status: '', specialization: '' });
                }}
              >
                <RefreshIcon />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabla */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={teachers}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={total}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowClick={(params) => navigate(`/teachers/${params.id}`)}
          disableRowSelectionOnClick
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        />
      </Paper>

      {/* Bulk Rate Dialog */}
      <Dialog open={bulkRateDialogOpen} onClose={() => setBulkRateDialogOpen(false)}>
        <DialogTitle>Actualizar Tarifas Masivamente</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Se actualizarán {bulkRateData.teacher_ids.length} profesor(es) seleccionado(s)
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Nueva Tarifa por Hora (RUB)"
            type="number"
            fullWidth
            value={bulkRateData.hourly_rate}
            onChange={(e) => setBulkRateData({
              ...bulkRateData,
              hourly_rate: parseFloat(e.target.value)
            })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkRateDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleBulkRateUpdate} variant="contained">
            Actualizar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            ¿Está seguro de eliminar al profesor{' '}
            <strong>
              {selectedTeacher?.first_name} {selectedTeacher?.last_name}
            </strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Esta acción no se puede deshacer. Se eliminarán también todos los pagos asociados.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeacherList;